import { useCallback, useEffect, useRef, useState } from "react"

import { roundHalfUp } from "@utils/roundHalfUp"

import { BULLET_CHARACTER } from "@particles/Bullet/Bullet"

import { Button } from "@atoms/index"

import AlertBanner from "@molecules/AlertBanner/AlertBanner"
import CheckboxCard from "@molecules/CheckboxCard/CheckboxCard"
import Dialog from "@molecules/Dialog/Dialog"

import styles from "./BillVendorCreditLinePickerDialog.module.scss"
import {
    BillVendorCreditLinePickerDialogProps,
    BillVendorCreditLinePickerLineItem,
} from "./BillVendorCreditLinePickerDialog.types"

// Read what the vendor credits holding a bill line took from it. Every total is rounded back to the decimal places the
// server keeps (three for quantities, two for amounts) so the decimal strings add up without float drift. A line is
// fully consumed once either its quantity or its amount is used up, since the server refuses more credit at that point.
function getLineItemCreditState(lineItem: BillVendorCreditLinePickerLineItem) {
    const lineQuantity = parseFloat(lineItem.quantity)
    const lineAmount = roundHalfUp(lineQuantity * parseFloat(lineItem.price), 2)

    let creditedQuantity = 0
    let creditedAmount = 0
    lineItem.vendor_credits.forEach((vendorCredit) => {
        creditedQuantity = roundHalfUp(creditedQuantity + parseFloat(vendorCredit.quantity), 3)
        creditedAmount = roundHalfUp(creditedAmount + parseFloat(vendorCredit.amount), 2)
    })

    const vendorCreditIDs = lineItem.vendor_credits.map((vendorCredit) => vendorCredit.custom_id || vendorCredit.id)

    return {
        availableQuantity: roundHalfUp(lineQuantity - creditedQuantity, 3),
        // The server refuses more credit once either the quantity or the money is used up, so either one closes the line
        isFullyConsumed: creditedQuantity >= lineQuantity || creditedAmount >= lineAmount,
        supportText:
            vendorCreditIDs.length === 0
                ? ""
                : `${creditedQuantity} of ${lineQuantity} credited ${BULLET_CHARACTER} ${vendorCreditIDs.join(", ")}`,
    }
}

export default function BillVendorCreditLinePickerDialog(props: BillVendorCreditLinePickerDialogProps) {
    const { isOpen, onOpenChange, lineItems, onCreateVendorCredit } = props

    const lineItemsWithCreditState = lineItems.map((lineItem) => ({
        lineItem,
        creditState: getLineItemCreditState(lineItem),
    }))

    const isFullyCredited = lineItemsWithCreditState.every(({ creditState }) => creditState.isFullyConsumed)

    const [selectedLineItemIDs, setSelectedLineItemIDs] = useState<number[]>([])
    const [hasSelectionError, setHasSelectionError] = useState(false)

    // Store onOpenChange in a ref so the callback identity stays stable
    // across renders. The Popover's useEffect depends on onOpenChange,
    // and an unstable reference causes an infinite re-render loop.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const stableOnOpenChange = useCallback((isNowOpen: boolean) => {
        onOpenChangeRef.current(isNowOpen)
    }, [])

    // Each opening starts with nothing checked.
    useEffect(() => {
        if (isOpen) {
            setSelectedLineItemIDs([])
            setHasSelectionError(false)
        }
    }, [isOpen])

    const handleToggle = (lineItemID: number) => {
        setSelectedLineItemIDs((prevSelected) =>
            prevSelected.includes(lineItemID)
                ? prevSelected.filter((selectedID) => selectedID !== lineItemID)
                : [...prevSelected, lineItemID],
        )
    }

    const handleCreateVendorCredit = () => {
        if (selectedLineItemIDs.length === 0) {
            setHasSelectionError(true)
        } else {
            onCreateVendorCredit(selectedLineItemIDs)
        }
    }

    return (
        <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={stableOnOpenChange}>
            <Dialog.Content>
                <Dialog.Header.Root>
                    <Dialog.Header.Title title="Create Vendor Credit" />
                    <Dialog.Header.CloseButton />
                </Dialog.Header.Root>
                <Dialog.Body>
                    <div className={styles.body}>
                        {isFullyCredited ? (
                            <AlertBanner
                                type="card"
                                status="info"
                                message="Every line on this bill is already fully credited on a vendor credit."
                            />
                        ) : (
                            <p>Select which lines you&apos;d like to credit:</p>
                        )}
                        {lineItemsWithCreditState.map(({ lineItem, creditState }) => (
                            <CheckboxCard
                                key={lineItem.id}
                                checked={selectedLineItemIDs.includes(lineItem.id)}
                                onCheckedChange={() => handleToggle(lineItem.id)}
                                label={`${lineItem.description} (Qty: ${creditState.availableQuantity})`}
                                showLabel={true}
                                supportText={creditState.supportText}
                                showSupportText={!!creditState.supportText}
                                isControlled={false}
                                isDisabled={creditState.isFullyConsumed}
                            />
                        ))}
                        {hasSelectionError && (
                            <span className={styles.errorMessage}>Please select at least one line to credit.</span>
                        )}
                    </div>
                </Dialog.Body>
                <Dialog.Footer.Root>
                    <Button
                        variant="solid"
                        size="lg"
                        onClick={handleCreateVendorCredit}
                        isFullWidth={true}
                        isDisabled={isFullyCredited}
                    >
                        Create Credit
                    </Button>
                </Dialog.Footer.Root>
            </Dialog.Content>
        </Dialog.Root>
    )
}
